import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface Vendor {
  readonly vendorId: string;
  readonly vendorCode: string;
  readonly vendorName: string;
  readonly contactPerson: string;
  readonly mobileNumber: string;
  readonly email: string;
  readonly gstNumber: string | null;
  readonly performanceRating: number | null;
  readonly effectiveFromDate: string;
  readonly effectiveToDate: string;
}

/**
 * `GET /v1/master/vendors` answers with a paginated envelope, not a bare array —
 * verified against the running server. Treating it as an array threw
 * `r.map is not a function` and took the whole screen down.
 */
interface VendorPage {
  readonly data?: readonly Vendor[];
}

export function VendorListPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { day } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<readonly Vendor[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [territory, setTerritory] = useState('');
  const [compliantOnly, setCompliantOnly] = useState(true);

  useEffect(() => {
    let alive = true;
    const params = new URLSearchParams();
    if (territory) params.set('territoryId', territory);
    params.set('compliantOnly', String(compliantOnly));
    apiRequest<VendorPage | readonly Vendor[]>(`/v1/master/vendors?${params.toString()}`, { token })
      .then((r) => {
        if (!alive) return;
        setRows(Array.isArray(r) ? (r as readonly Vendor[]) : ((r as VendorPage).data ?? []));
      })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token, territory, compliantOnly]);

  if (error) return <main aria-label="vendor-list"><p role="alert">{error}</p></main>;
  if (!rows) return <main aria-label="vendor-list"><p>{t('vendors.list.loading')}</p></main>;

  return (
    <main aria-label="vendor-list">
      <h1>{t('vendors.list.title')}</h1>
      <section aria-label="filters">
        <label htmlFor="terr">{t('vendors.list.territory')}</label>
        <input id="terr" aria-label="territory" value={territory} onChange={(e) => setTerritory(e.target.value)} />
        <label htmlFor="comp">
          <input id="comp" type="checkbox" aria-label="compliant-only" checked={compliantOnly} onChange={(e) => setCompliantOnly(e.target.checked)} />
          {t('vendors.list.compliantOnly')}
        </label>
        <Link to="/admin/vendors/new" aria-label="vendor-new-link">{t('vendors.list.new')}</Link>
      </section>
      {rows.length === 0 ? (
        <p aria-label="empty">{t('vendors.list.empty')}</p>
      ) : (
        <table aria-label="vendor-table">
          <thead>
            <tr><th>{t('vendors.list.col.code')}</th><th>{t('vendors.list.col.name')}</th><th>{t('vendors.list.col.gst')}</th><th>{t('vendors.list.col.rating')}</th><th>{t('vendors.list.col.validity')}</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map((v) => (
              <tr key={v.vendorId} aria-label={`row-${v.vendorId}`}>
                <td>{v.vendorCode}</td>
                <td>{v.vendorName}</td>
                <td>{v.gstNumber ?? '—'}</td>
                <td>{v.performanceRating ?? '—'}</td>
                <td>{day(v.effectiveFromDate)} → {day(v.effectiveToDate)}</td>
                <td><Link to={`/admin/vendors/${v.vendorId}`} aria-label={`open-${v.vendorId}`}>{t('vendors.list.open')}</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
